import { useRef, useState, useEffect } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";

// Opt-in ambient horror soundscape (synthesized) OR uploaded background music. Never autoplays.
export function AudioToggle() {
  const [on, setOn] = useState(false);
  const [bgUrl, setBgUrl] = useState("");
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const nodesRef = useRef([]);
  const timersRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setBgUrl(data.bg_music_url || "")).catch(() => {});
  }, []);

  const makeNoise = (ctx, seconds = 2) => {
    const buf = ctx.createBuffer(1, seconds * ctx.sampleRate, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  };

  const start = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const master = ctx.createGain();
    master.gain.value = 0.0;
    master.connect(ctx.destination);

    // 60Hz mains hum
    const hum = ctx.createOscillator();
    hum.type = "sine"; hum.frequency.value = 60;
    const humG = ctx.createGain(); humG.gain.value = 0.06;
    hum.connect(humG).connect(master); hum.start();

    // high CRT whine
    const whine = ctx.createOscillator();
    whine.type = "sine"; whine.frequency.value = 15200;
    const whineG = ctx.createGain(); whineG.gain.value = 0.012;
    whine.connect(whineG).connect(master); whine.start();

    // low room tone (lowpass noise)
    const roomBuf = makeNoise(ctx, 3);
    const room = ctx.createBufferSource(); room.buffer = roomBuf; room.loop = true;
    const roomLP = ctx.createBiquadFilter(); roomLP.type = "lowpass"; roomLP.frequency.value = 520;
    const roomG = ctx.createGain(); roomG.gain.value = 0.05;
    room.connect(roomLP).connect(roomG).connect(master); room.start();

    // TV static (bandpass hiss)
    const staticBuf = makeNoise(ctx, 3);
    const stat = ctx.createBufferSource(); stat.buffer = staticBuf; stat.loop = true;
    const statBP = ctx.createBiquadFilter(); statBP.type = "bandpass"; statBP.frequency.value = 4200; statBP.Q.value = 0.6;
    const statG = ctx.createGain(); statG.gain.value = 0.035;
    stat.connect(statBP).connect(statG).connect(master); stat.start();

    nodesRef.current = [hum, whine, room, stat];
    masterRef.current = master;
    ctxRef.current = ctx;

    master.gain.linearRampToValueAtTime(0.85, ctx.currentTime + 1.5);

    // --- scheduled random events ---
    const rand = (a, b) => a + Math.random() * (b - a);

    // Tape rewind: pitch-swept noise
    const rewind = () => {
      const t = ctx.currentTime;
      const src = ctx.createBufferSource(); src.buffer = makeNoise(ctx, 1.2);
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.Q.value = 8;
      bp.frequency.setValueAtTime(1800, t);
      bp.frequency.exponentialRampToValueAtTime(400, t + 1.0);
      const g = ctx.createGain(); g.gain.setValueAtTime(0.0, t);
      g.gain.linearRampToValueAtTime(0.14, t + 0.05);
      g.gain.linearRampToValueAtTime(0.0, t + 1.05);
      src.connect(bp).connect(g).connect(master);
      src.start(t); src.stop(t + 1.2);
    };

    // Whistling: high sine glide
    const whistle = () => {
      const t = ctx.currentTime;
      const o = ctx.createOscillator(); o.type = "sine";
      o.frequency.setValueAtTime(rand(900, 1400), t);
      o.frequency.exponentialRampToValueAtTime(rand(1800, 2600), t + 1.4);
      const g = ctx.createGain(); g.gain.setValueAtTime(0.0, t);
      g.gain.linearRampToValueAtTime(0.05, t + 0.4);
      g.gain.linearRampToValueAtTime(0.0, t + 1.6);
      o.connect(g).connect(master); o.start(t); o.stop(t + 1.7);
    };

    // Whisper: filtered noise with amplitude envelope
    const whisper = () => {
      const t = ctx.currentTime;
      const src = ctx.createBufferSource(); src.buffer = makeNoise(ctx, 1.5);
      const bp = ctx.createBiquadFilter(); bp.type = "bandpass";
      bp.frequency.value = rand(1200, 2200); bp.Q.value = 4;
      const g = ctx.createGain(); g.gain.value = 0;
      // pulsing envelope like syllables
      let tt = t;
      for (let i = 0; i < 5; i++) {
        g.gain.setValueAtTime(0.0, tt);
        g.gain.linearRampToValueAtTime(rand(0.05, 0.11), tt + 0.08);
        g.gain.linearRampToValueAtTime(0.0, tt + 0.22);
        tt += rand(0.28, 0.5);
      }
      src.connect(bp).connect(g).connect(master);
      src.start(t); src.stop(tt + 0.2);
    };

    // Crackle: short static bursts
    const crackle = () => {
      const t = ctx.currentTime;
      const src = ctx.createBufferSource(); src.buffer = makeNoise(ctx, 0.3);
      const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 3000;
      const g = ctx.createGain(); g.gain.setValueAtTime(0.18, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      src.connect(hp).connect(g).connect(master);
      src.start(t); src.stop(t + 0.3);
    };

    const schedule = (fn, min, max) => {
      const loop = () => {
        fn();
        const id = setTimeout(loop, rand(min, max) * 1000);
        timersRef.current.push(id);
      };
      const id = setTimeout(loop, rand(min, max) * 1000);
      timersRef.current.push(id);
    };
    schedule(rewind, 12, 24);
    schedule(whistle, 9, 20);
    schedule(whisper, 6, 14);
    schedule(crackle, 3, 8);
  };

  const stop = () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    masterRef.current.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.4);
    setTimeout(() => ctx.close(), 500);
    ctxRef.current = null;
  };

  const toggle = () => {
    if (on) {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } else { stop(); }
    } else if (bgUrl) {
      const a = new Audio(bgUrl); a.loop = true; a.volume = 0.5; a.play().catch(() => {}); audioRef.current = a;
    } else { start(); }
    setOn(!on);
  };

  return (
    <button onClick={toggle} data-testid="audio-toggle"
      title={on ? "Ztlumit atmosféru" : "Zapnout atmosféru"}
      className={`p-1.5 transition-colors ${on ? "text-nv" : "text-neutral-400 hover:text-neutral-200"}`}>
      {on ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
    </button>
  );
}
